import type { AppearanceConfig } from '../components/MainMenu/sections/admin/EnvironmentConfig/types/EnvironmentConfig.types'
import type { SevvThemeTokens } from './sevvThemeTokenModel'
import { resolveEffectiveChrome } from './sevvThemeTokenModel'
import { resolveThemeMode } from './resolveWorkspaceTheme'

/** Primary RGB for box-shadow rgba() approximations */
function hexToRgbTriplet(hex: string): string {
  const h = hex.replace('#', '')
  if (h.length === 3) {
    const r = parseInt(h[0] + h[0], 16)
    const g = parseInt(h[1] + h[1], 16)
    const b = parseInt(h[2] + h[2], 16)
    return `${r}, ${g}, ${b}`
  }
  if (h.length !== 6) return '249, 116, 21'
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return `${r}, ${g}, ${b}`
}

/**
 * Applies Sevv CSS custom properties so SCSS `var(--sevv-*, fallback)` picks up workspace tokens.
 */
export function applySevvCssVariables(
  target: HTMLElement,
  tokens: SevvThemeTokens,
  appearance: AppearanceConfig,
  prefersDark: boolean
): void {
  const mode = resolveThemeMode(appearance, prefersDark)
  const p =
    mode === 'dark' && tokens.darkPalette
      ? { ...tokens.palette, ...tokens.darkPalette }
      : tokens.palette

  const font =
    appearance.typography.fontFamily ||
    "'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif"

  const fontSizeScale =
    appearance.typography.fontSize === 'small'
      ? 0.875
      : appearance.typography.fontSize === 'large'
        ? 1.125
        : 1

  const rgb = hexToRgbTriplet(p.primary)
  const chrome = resolveEffectiveChrome(tokens, p, mode)

  const set = (name: string, value: string) => target.style.setProperty(name, value)

  set('--sevv-primary', p.primary)
  set('--sevv-primary-light', p.primaryLight)
  set('--sevv-primary-dark', p.primaryDark)
  set('--sevv-primary-pale', p.primaryPale)
  set('--sevv-secondary', p.secondary)
  set('--sevv-success', p.success)
  set('--sevv-warning', p.warning)
  set('--sevv-error', p.error)
  set('--sevv-info', p.info)
  set('--sevv-bg-default', p.backgroundDefault)
  set('--sevv-bg-paper', p.backgroundPaper)
  set('--sevv-text-primary', p.textPrimary)
  set('--sevv-text-secondary', p.textSecondary)
  set('--sevv-divider', p.divider)
  set('--sevv-font-family', font)
  set('--sevv-font-size-scale', String(fontSizeScale))
  set('--sevv-shadow-primary', `0 4px 14px 0 rgba(${rgb}, 0.3)`)
  set('--sevv-shadow-primary-lg', `0 10px 25px 0 rgba(${rgb}, 0.4)`)

  set('--sevv-gray-50', p.backgroundDefault)
  set('--sevv-gray-100', mode === 'dark' ? '#2a2a2a' : '#f5f5f5')
  set('--sevv-gray-200', p.divider)
  set('--sevv-gray-400', mode === 'dark' ? '#888888' : '#bdbdbd')
  set('--sevv-gray-600', p.textSecondary)
  set('--sevv-gray-800', p.textPrimary)
  set('--sevv-white', mode === 'dark' ? p.backgroundPaper : '#ffffff')
  // Text and glyphs on a --sevv-warning fill. White in both modes, as MUI's
  // warning.contrastText is: --sevv-white turns into dark paper in dark mode.
  set('--sevv-on-warning', '#ffffff')

  set('--sevv-overlay-backdrop', chrome.overlayBackdrop)
  set('--sevv-video-tile-bg', chrome.videoTileBackground)
  set('--sevv-surface-overlay', chrome.surfaceOverlay)
  set('--sevv-border-subtle', chrome.borderSubtle)
  set('--sevv-shell-sidebar-bg', chrome.shellSidebarBackground)
  set('--sevv-shell-header-bg', chrome.shellHeaderBackground)

  set('--sevv-dialog-title-bg', chrome.dialogTitleBackground)
  set('--sevv-dialog-title-color', chrome.dialogTitleColor)
  set('--sevv-dialog-title-border-bottom', chrome.dialogTitleBorderBottom)
  set('--sevv-dialog-actions-bg', chrome.dialogActionsBackground)
  set('--sevv-dialog-actions-border-top', chrome.dialogActionsBorderTop)

  const g = tokens.surfaces?.gameInRoom ?? {}
  const ts = g.toolbarGradientStart?.trim() || p.backgroundPaper
  const te = g.toolbarGradientEnd?.trim() || p.primaryPale
  const primaryTint10 = `rgba(${rgb}, 0.1)`
  const primaryTint20 = `rgba(${rgb}, 0.2)`
  set('--sevv-surface-game-toolbar-bg', `linear-gradient(135deg, ${ts} 0%, ${te} 100%)`)
  set('--sevv-surface-game-toolbar-border', g.toolbarBorder?.trim() || primaryTint20)
  set('--sevv-surface-game-icon-btn-bg', g.iconButtonBg?.trim() || primaryTint10)
  set('--sevv-surface-game-icon-btn-hover-bg', g.iconButtonHoverBg?.trim() || primaryTint20)
  set(
    '--sevv-surface-game-menu-bg',
    g.menuPaperBg?.trim() || `linear-gradient(135deg, ${ts} 0%, ${te} 100%)`
  )
  set('--sevv-surface-game-panel-paper-bg', g.panelPaperBg?.trim() || p.backgroundPaper)
  set('--sevv-surface-game-accent-muted', g.accentMuted?.trim() || p.textSecondary)
  set('--sevv-surface-game-text-strong', p.textPrimary)

  set(
    '--sevv-surface-game-dialog-title-bg',
    g.dialogTitleBackground?.trim() || chrome.dialogTitleBackground
  )
  set('--sevv-surface-game-dialog-title-color', g.dialogTitleColor?.trim() || chrome.dialogTitleColor)
  set(
    '--sevv-surface-game-dialog-title-border-bottom',
    g.dialogTitleBorderBottom?.trim() || chrome.dialogTitleBorderBottom
  )

  target.setAttribute('data-sevv-theme', mode)
}

export function clearSevvCssVariables(target: HTMLElement): void {
  const names = [
    '--sevv-primary',
    '--sevv-primary-light',
    '--sevv-primary-dark',
    '--sevv-primary-pale',
    '--sevv-secondary',
    '--sevv-success',
    '--sevv-warning',
    '--sevv-error',
    '--sevv-info',
    '--sevv-bg-default',
    '--sevv-bg-paper',
    '--sevv-text-primary',
    '--sevv-text-secondary',
    '--sevv-divider',
    '--sevv-font-family',
    '--sevv-font-size-scale',
    '--sevv-shadow-primary',
    '--sevv-shadow-primary-lg',
    '--sevv-gray-50',
    '--sevv-gray-100',
    '--sevv-gray-200',
    '--sevv-gray-400',
    '--sevv-gray-600',
    '--sevv-gray-800',
    '--sevv-white',
    '--sevv-on-warning',
    '--sevv-overlay-backdrop',
    '--sevv-video-tile-bg',
    '--sevv-surface-overlay',
    '--sevv-border-subtle',
    '--sevv-shell-sidebar-bg',
    '--sevv-shell-header-bg',
    '--sevv-dialog-title-bg',
    '--sevv-dialog-title-color',
    '--sevv-dialog-title-border-bottom',
    '--sevv-dialog-actions-bg',
    '--sevv-dialog-actions-border-top',
    '--sevv-surface-game-toolbar-bg',
    '--sevv-surface-game-toolbar-border',
    '--sevv-surface-game-icon-btn-bg',
    '--sevv-surface-game-icon-btn-hover-bg',
    '--sevv-surface-game-menu-bg',
    '--sevv-surface-game-panel-paper-bg',
    '--sevv-surface-game-accent-muted',
    '--sevv-surface-game-text-strong',
    '--sevv-surface-game-dialog-title-bg',
    '--sevv-surface-game-dialog-title-color',
    '--sevv-surface-game-dialog-title-border-bottom',
  ]
  names.forEach((n) => target.style.removeProperty(n))
  target.removeAttribute('data-sevv-theme')
}
